import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search, Check } from 'lucide-react';
import { COUNTRIES } from '../data/countries';

/**
 * CountryCombobox — searchable single-select dropdown for country picking.
 * Plain Tailwind, no external dep, matches the wizard's compact form style.
 *
 * value: country name string ("India")
 * onChange: (newName) => void
 */
export default function CountryCombobox({ value, onChange, label = 'Country *', testid = 'country-combobox' }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
    else setQ('');
  }, [open]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return COUNTRIES;
    return COUNTRIES.filter(c => c.name.toLowerCase().includes(s) || c.code.toLowerCase() === s);
  }, [q]);

  return (
    <div ref={wrapRef} className="relative" data-testid={testid}>
      <label className="text-[9px] text-zinc-500 uppercase tracking-wider">{label}</label>
      <button type="button" onClick={() => setOpen(o => !o)} data-testid={`${testid}-trigger`}
        className="w-full mt-1 px-2 py-1.5 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-white text-left flex items-center justify-between hover:border-zinc-700 focus:outline-none focus:border-violet-500/50">
        <span className={value ? 'text-white' : 'text-zinc-500'}>{value || 'Select country…'}</span>
        <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-zinc-900 border border-zinc-800 rounded-sm shadow-2xl shadow-black/50 overflow-hidden">
          <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-zinc-800 bg-zinc-950">
            <Search className="w-3 h-3 text-zinc-500" />
            <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search country…" data-testid={`${testid}-search`}
              className="flex-1 bg-transparent text-xs text-white outline-none placeholder:text-zinc-600" />
          </div>
          <div className="max-h-64 overflow-y-auto" data-testid={`${testid}-list`}>
            {filtered.length === 0 && <p className="text-[11px] text-zinc-500 px-3 py-3">No matches</p>}
            {filtered.map(c => {
              const selected = value === c.name;
              return (
                <button key={c.code} type="button"
                  onClick={() => { onChange(c.name); setOpen(false); }}
                  data-testid={`${testid}-opt-${c.code}`}
                  className={`w-full text-left px-2.5 py-1.5 text-xs flex items-center gap-2 hover:bg-violet-500/10 ${selected ? 'bg-violet-500/15 text-violet-200' : 'text-zinc-200'}`}>
                  <span className="text-[10px] text-zinc-500 font-mono w-7">{c.code}</span>
                  <span className="flex-1 truncate">{c.name}</span>
                  {selected && <Check className="w-3 h-3 text-violet-300" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
